import { MfaFactor, type BindMfaPayload, type VerifyMfaPayload } from '@logto/schemas';
import { useCallback } from 'react';

import {
  bindMfa,
  issueTrustedDevice,
  submitInteraction,
  verifyMfa,
  verifyMfaOnly,
} from '@/apis/experience';
import { UserMfaFlow } from '@/types';

import useApi from './use-api';
import useErrorHandler, { type ErrorHandlers } from './use-error-handler';
import useGlobalRedirectTo from './use-global-redirect-to';
import usePreSignInErrorHandler from './use-pre-sign-in-error-handler';
import useStartTotpBinding from './use-start-totp-binding';
import useStartWebAuthnProcessing from './use-start-webauthn-processing';

export type SendMfaPayloadApiOptions =
  | {
      flow: UserMfaFlow.MfaBinding;
      payload: BindMfaPayload;
      verificationId: string;
    }
  | {
      flow: UserMfaFlow.MfaVerification;
      payload: VerifyMfaPayload;
      verificationId?: string;
      /** TMMIN MFA — request a trusted-device cookie after verify succeeds. */
      rememberDevice?: boolean;
      /**
       * TMMIN MFA — verify-then-enroll. After this verification succeeds, start
       * passkey (WebAuthn) binding instead of submitting the interaction. The
       * just-verified factor satisfies `verifiedInteractionGuard`, so binding a
       * brand-new passkey is allowed without weakening MFA.
       */
      pendingPasskeyEnrollment?: boolean;
      /**
       * TMMIN MFA — verify-then-enroll, opposite direction. After this
       * verification succeeds (e.g. passkey), start TOTP binding instead of
       * submitting the interaction.
       */
      pendingTotpEnrollment?: boolean;
    };

const sendMfaPayloadApi = async ({ flow, payload, verificationId }: SendMfaPayloadApiOptions) => {
  if (flow === UserMfaFlow.MfaBinding) {
    return bindMfa(payload, verificationId);
  }
  return verifyMfa(payload, verificationId);
};

const verifyMfaOnlyApi = async ({
  payload,
  verificationId,
}: {
  payload: VerifyMfaPayload;
  verificationId?: string;
}) => verifyMfaOnly(payload, verificationId);

const useSendMfaPayload = () => {
  const asyncSendMfaPayload = useApi(sendMfaPayloadApi);
  const asyncVerifyMfaOnly = useApi(verifyMfaOnlyApi);
  const asyncIssueTrustedDevice = useApi(issueTrustedDevice);
  const asyncSubmitInteraction = useApi(submitInteraction);
  const startWebAuthnProcessing = useStartWebAuthnProcessing();
  const startTotpBinding = useStartTotpBinding();
  const preSignInErrorHandler = usePreSignInErrorHandler({ replace: true });
  const handleError = useErrorHandler();
  const redirectTo = useGlobalRedirectTo();

  return useCallback(
    async (
      apiOptions: SendMfaPayloadApiOptions,
      errorHandlers?: ErrorHandlers,
      errorCallback?: () => void
    ) => {
      // TMMIN MFA — verify-then-enroll. Verify the existing factor WITHOUT
      // submitting, then start passkey binding. The verified record satisfies
      // the backend's `verifiedInteractionGuard`, and the binding step submits
      // the interaction on success.
      if (
        apiOptions.flow === UserMfaFlow.MfaVerification &&
        (apiOptions.pendingPasskeyEnrollment || apiOptions.pendingTotpEnrollment)
      ) {
        const [verifyError] = await asyncVerifyMfaOnly({
          payload: apiOptions.payload,
          verificationId: apiOptions.verificationId,
        });

        if (verifyError) {
          await handleError(verifyError, { ...errorHandlers, ...preSignInErrorHandler });
          errorCallback?.();
          return;
        }

        if (apiOptions.pendingTotpEnrollment) {
          await startTotpBinding({
            availableFactors: [MfaFactor.TOTP],
            skippable: false,
          });
          return;
        }

        await startWebAuthnProcessing(UserMfaFlow.MfaBinding, {
          availableFactors: [MfaFactor.WebAuthn],
          skippable: false,
        });
        return;
      }

      const rememberDevice =
        apiOptions.flow === UserMfaFlow.MfaVerification &&
        apiOptions.payload.type !== MfaFactor.WebAuthn
          ? Boolean(apiOptions.rememberDevice)
          : false;

      // TMMIN MFA — when user opted to remember device, defer submitInteraction
      // so we can issue the trusted-device cookie between verify and submit.
      // Takes precedence over the passkey-prompt branch.
      if (rememberDevice && apiOptions.flow === UserMfaFlow.MfaVerification) {
        const [verifyError] = await asyncVerifyMfaOnly({
          payload: apiOptions.payload,
          verificationId: apiOptions.verificationId,
        });

        if (verifyError) {
          await handleError(verifyError, { ...errorHandlers, ...preSignInErrorHandler });
          errorCallback?.();
          return;
        }

        // Best-effort cookie issuance — failure here must not block sign-in.
        const [cookieError] = await asyncIssueTrustedDevice();
        if (cookieError) {
          console.warn('Failed to issue trusted-device cookie', cookieError);
        }

        const [submitError, result] = await asyncSubmitInteraction();
        if (submitError) {
          await handleError(submitError, { ...errorHandlers, ...preSignInErrorHandler });
          errorCallback?.();
          return;
        }
        if (result) {
          await redirectTo(result.redirectTo);
        }
        return;
      }

      // TMMIN MFA — auto post-verify "Add passkey" prompt is disabled. Passkey
      // setup only happens when the user explicitly chooses it from MfaChoose.

      const [error, result] = await asyncSendMfaPayload(apiOptions);

      if (error) {
        await handleError(error, {
          ...errorHandlers,
          ...preSignInErrorHandler,
        });
        errorCallback?.();
        return;
      }

      if (result) {
        await redirectTo(result.redirectTo);
      }
    },
    [
      asyncSendMfaPayload,
      asyncVerifyMfaOnly,
      asyncIssueTrustedDevice,
      asyncSubmitInteraction,
      startWebAuthnProcessing,
      startTotpBinding,
      handleError,
      preSignInErrorHandler,
      redirectTo,
    ]
  );
};

export default useSendMfaPayload;
